import React, { useState, useEffect, useCallback } from 'react';
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import {
  Typography,
  Container,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Box,
  Alert
} from '@mui/material';
import axios from 'axios';

function Confirmation() {
  const [searchParams] = useSearchParams();
  const params = Array.from(searchParams.entries()).map(([key, value]) => ({ key, value }));
  const raw = Object.fromEntries(params.map(({ key, value }) => [key, value]));
  const orderId = raw.orderld
  const transactionId = raw.transactionld;

  const [status, setStatus] = useState('PENDING');
  const [transaction, setTransaction] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);

  const pollTransactionStatus = useCallback(async () => {
    if (!orderId) {
      setStatus('FAILED');
      setError('No order ID found in the URL.');
      return;
    }
    try {
      const response = await axios.get(`/api/transaction-status/${orderId}`);
      const data = response.data;
      setTransaction(data);
      if (data.status === 'COMPLETED' || data.status === 'FAILED') {
        setStatus(data.status);
      }
    } catch (err) {
      setStatus('FAILED');
      setError('An error occurred while fetching transaction status.');
    }
  }, [orderId]);

  useEffect(() => {
    console.log(searchParams.get("orderId"));
    console.log(searchParams.get("transactionId"));
    if (!orderId) {
        setStatus('FAILED');
        setError('No order specified.');
        return;
    }

    const pollingInterval = setInterval(pollTransactionStatus, 5000);
    const timeout = setTimeout(() => {
      clearInterval(pollingInterval);
      if (status === 'PENDING') {
        setStatus('TIMED_OUT');
      }
    }, 60000); // 60 seconds

    // Initial check
    pollTransactionStatus();

    return () => {
      clearInterval(pollingInterval);
      clearTimeout(timeout);
    };
  }, [orderId, status, pollTransactionStatus]);

  useEffect(() => {
    if (status === 'COMPLETED' && orderId) {
      const fetchOrderDetails = async () => {
        try {
          const response = await axios.get(`/api/order-details/${orderId}`);
          const data = response.data;
          setOrderDetails(data);
        } catch (err) {
          setError('An error occurred while fetching order details.');
        }
      };
      fetchOrderDetails();
    }
  }, [status, orderId]);

  const renderContent = () => {
    if (status === 'PENDING') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Processing your order...
          </Typography>
        </Box>
      );
    }

    if (status === 'FAILED') {
      return (
          <Alert severity="error" sx={{ mt: 4 }}>
              <Typography variant="h6">Purchase Failed</Typography>
              <Typography variant="body1">
                  {error || 'An unexpected error occurred. Please try again.'}
              </Typography>
          </Alert>
      );
    }

    if (status === 'TIMED_OUT') {
      return (
        <Alert severity="warning" sx={{ mt: 4 }}>
            <Typography variant="h6">Transaction Timed Out</Typography>
            <Typography variant="body1">
                The transaction is taking longer than expected. Please check your email for a confirmation. If you don't receive one, please try the transaction again later.
            </Typography>
        </Alert>
      );
    }

    if (status === 'COMPLETED') {
      const downloadLinks = transaction && transaction.download_link ? JSON.parse(transaction.download_link) : [];
      const digitalItems = orderDetails && orderDetails.lineItems ? orderDetails.lineItems.filter(item =>
        downloadLinks.length > 0
      ) : [];

      return (
        <>
          <Typography variant="h4" gutterBottom>
            Thank you for your purchase!
          </Typography>
          <Typography variant="body1">
            You will receive an email confirmation shortly.
          </Typography>
          {digitalItems.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Download Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {digitalItems.map((item, index) => (
                    <TableRow key={item.uid}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        <Link href={downloadLinks[index]} target="_blank" rel="noopener noreferrer">
                          Download
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <Container>
      {renderContent()}
    </Container>
  );
}

export default Confirmation;
